import React, {
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react';
import {
	message,
	Dropdown,
	Menu,
} from 'antd';
import {
	Avatar, Button, Space, Tag,
} from 'antd-mobile';

import { useHistory } from 'react-router-dom';
import {
	ClockCircleOutlined,
	ExclamationCircleFilled,
	CheckCircleFilled,
	CheckCircleOutlined,
	FullscreenOutlined,
	FullscreenExitOutlined,
	LoginOutlined,
	LogoutOutlined,
	ReloadOutlined,
	UpOutlined,
	DownOutlined,
	LockFilled,
} from '@ant-design/icons';
import { ReactComponent as CQLogo } from '../../assets/img/cq-logo.svg';
import ChitkaraLogo from '../../assets/img/chitkara.png';
import userIcon from '../../assets/icons/user-icon.svg';
import { ReactComponent as LogoutIcon } from '../../assets/icons/logout-icon.svg';

import {
	APP_CONFIG, NetworkStatus, QuestionStatus, isElectronApp,
} from '../../config';
import { FontChangeButton } from '../../components/font-change-button';
import { NetworkStats } from '../../components/network-stats';
import { getIdentityCodeCharColor } from '../../libs/utils';

const Logo = (APP_CONFIG.isChitkara) ? <img src={ChitkaraLogo} alt="Logo" height={35} /> : <CQLogo height={35} />;

interface AppHeaderProps {
	quizName: string
	quizData: QuizData | null
	sessionData: SessionData | null
	hasQuizStarted?: boolean
	timeLeft?: number
	tabSwitchCount?: number
	tabSwitchInCount?: number
	fullScreenInCount?: number
	fullScreenOutCount?: number
	totalSubmittedQuestions?: number
	showHome?: boolean
	quizSegments?: QuizDashboardSegmentData[]
	currentQuestionId?: string
	nextLink?: string | null
	previousLink?: string | null
	networkStatus: NetworkStatus,
	networkStats: NetworkStats | null,
	identityCheckCode?: string | null,
	setLoading: (status: boolean) => Promise<void>,
	stopRecording: () => Promise<void>,
	submitQuiz?: (SumitQuizParms) => Promise<string | void>,
	setTimeLeft?: (time: number) => Promise<void>,
	getRemainingTime?: () => Promise<void>,
}

function TagWithIcon({ icon, text }: { icon: React.ReactNode, text: string | number }) {
	return (
		<Tag round color="#f4f4f4">
			<Space align="center" style={{ padding: '2px 4px', fontSize: '14px', color: '#333' }}>
				{icon}
				<span style={{ fontWeight: 500 }}>{text}</span>
			</Space>
		</Tag>
	);
}

export const AppHeader: React.FunctionComponent<AppHeaderProps> = (props) => {
	const {
		quizName,
		sessionData,
		quizData,
		timeLeft = 1,
		tabSwitchCount,
		tabSwitchInCount,
		fullScreenInCount,
		fullScreenOutCount,
		totalSubmittedQuestions = 0,
		hasQuizStarted,
		quizSegments,
		currentQuestionId,
		networkStatus,
		networkStats,
		identityCheckCode,
		setLoading,
		stopRecording,
		getRemainingTime,
	} = props;

	const loadingNetworkStat = networkStats === null;

	const history = useHistory();
	const refreshHandler = useCallback(async () => {
		if (networkStatus === NetworkStatus.Online) {
			setLoading(true);
			window.location.reload();
			return;
		}
		message.error('Please check your internet connection.');
	}, [networkStatus, setLoading]);

	const [timerText, setTimerText] = useState<string>('');
	const [offline, setOfflineStatus] = useState<boolean>(false);
	const [showConnectionStatus, setShowConnectionStatus] = useState<boolean>(false);
	const [navPopupVisible, setNavPopupVisible] = useState<boolean>(false);
	const [expandedSegments, setExpandedSegments] = useState<Set<string>>(new Set());
	const popupRef = useRef<HTMLDivElement>(null);

	// Compute current question global index
	const { currentQuestionNumber, totalQuestions } = useMemo(() => {
		if (!quizSegments || !currentQuestionId) {
			return { currentQuestionNumber: null, totalQuestions: quizData?.questions ?? 0 };
		}
		const allQuestions = quizSegments.flatMap((seg) => seg.questions);
		const foundIdx = allQuestions.findIndex((q) => q._id === currentQuestionId);
		return {
			currentQuestionNumber: foundIdx >= 0 ? foundIdx + 1 : null,
			totalQuestions: allQuestions.length || quizData?.questions || 0,
		};
	}, [quizSegments, currentQuestionId, quizData]);

	const totalAttemptedQuestions = useMemo(() => {
		if (totalSubmittedQuestions > 0) {
			return totalSubmittedQuestions;
		}
		if (!quizSegments) {
			return 0;
		}
		return quizSegments.reduce(
			(acc, segment) => acc
				+ segment.questions.filter((q) => q.status === QuestionStatus.Submitted).length,
			0,
		);
	}, [totalSubmittedQuestions, quizSegments]);

	const totalQuizQuestions = useMemo(() => {
		if (quizSegments) {
			return quizSegments.reduce((acc, segment) => acc + segment.questions.length, 0);
		}
		return quizData?.questions ?? 0;
	}, [quizSegments, quizData]);

	const isDashboardView = hasQuizStarted && !currentQuestionId;

	// Auto-expand the segment that contains the current question
	useEffect(() => {
		if (!quizSegments || !currentQuestionId) return undefined;
		const activeSegment = quizSegments.find(
			(seg) => seg.questions.some((q) => q._id === currentQuestionId),
		);
		if (activeSegment) {
			setExpandedSegments((prev) => new Set(Array.from(prev).concat(activeSegment._id)));
		}
		return undefined;
	}, [quizSegments, currentQuestionId]);

	// Close popup on outside click
	useEffect(() => {
		if (!navPopupVisible) return undefined;
		const handler = (e: MouseEvent) => {
			if (popupRef.current && !popupRef.current.contains(e.target as Node)) {
				setNavPopupVisible(false);
			}
		};
		document.addEventListener('mousedown', handler);
		return () => document.removeEventListener('mousedown', handler);
	}, [navPopupVisible]);

	useEffect(() => {
		if (timeLeft) {
			const hours = Math.floor(timeLeft / 3600);
			const minutes = Math.floor((timeLeft / 60) % 60);
			const seconds = timeLeft % 60;
			const hoursString = hours > 0 ? `${hours.toString().padStart(2, '0')}h ` : '';
			const minutesString = minutes.toString().padStart(2, '0');
			const secondsString = seconds.toString().padStart(2, '0');
			setTimerText(`${hoursString}${minutesString}m ${secondsString}s`);
			if (timeLeft === 299) {
				message.error('Hurry Up! Only 5 minutes are left to complete your test.');
			}
		}
	}, [timeLeft, setTimerText]);

	useEffect(() => {
		const intervalId: NodeJS.Timeout = setInterval(() => getRemainingTime?.(), 20000);
		return () => clearInterval(intervalId);
	}, [getRemainingTime]);

	const goToDashboard = useCallback(() => {
		history.push(`/test/${quizName}/dashboard`);
	}, [history, quizName]);

	const logoutUser = useCallback(() => {
		try {
			if (!navigator.onLine) {
				throw new Error('Please check your internet connection.');
			}
			history.push({
				state: { referrer: '' },
			});
			window.location.replace(`${APP_CONFIG.QuizServerURL}/logout`);
		} catch (error: any) {
			message.error(error.message);
		}
	}, [history]);

	useEffect(() => {
		let statusTimeOut: null | NodeJS.Timeout = null;
		switch (networkStatus) {
			case NetworkStatus.Offline: {
				setShowConnectionStatus(true);
				setOfflineStatus(true);
				break;
			}
			case NetworkStatus.Online: {
				setOfflineStatus(false);
				statusTimeOut = setTimeout(() => {
					setShowConnectionStatus(false);
				}, 3000);
				break;
			}
			default: {
				console.log('Network State Change');
			}
		}
		return () => {
			if (statusTimeOut) {
				clearInterval(statusTimeOut);
			}
		};
	}, [networkStatus]);

	const toggleSegment = (segmentId: string) => {
		setExpandedSegments((prev) => {
			const next = new Set(prev);
			if (next.has(segmentId)) {
				next.delete(segmentId);
			} else {
				next.add(segmentId);
			}
			return next;
		});
	};

	const ConnectionHeader = useMemo(() => {
		if (showConnectionStatus) {
			return (
				<>
					{
						offline ? (
							<Space style={{ width: '100%', padding: '0.5em' }} justify="center" direction="horizontal">
								<ExclamationCircleFilled style={{ color: 'red', fontSize: '18px' }} />
								<span className="OfflineStatusText">Your device lost its internet connection.</span>
							</Space>
						)
							: (
								<Space style={{ width: '100%', padding: '0.5em' }} justify="center" direction="horizontal">
									<CheckCircleFilled style={{ color: 'green', fontSize: '18px' }} />
									<span className="onlineStatusText">Your device is connected to the internet.</span>
								</Space>
							)
					}
				</>
			);
		}
		if (networkStats && networkStats.upload && networkStats.upload.speed < 0.3) {
			return (
				<Space style={{ width: '100%', padding: '0.5em' }} justify="center" direction="horizontal">
					<ExclamationCircleFilled style={{ color: 'red', fontSize: '18px' }} />
					<span className="OfflineStatusText">Your internet connection is poor.</span>
				</Space>
			);
		}
		if (networkStats && networkStats.upload && networkStats.upload.speed < 0.6) {
			return (
				<Space style={{ width: '100%', padding: '0.5em' }} justify="center" direction="horizontal">
					<ExclamationCircleFilled style={{ color: '#faad14', fontSize: '18px' }} />
					<span className="OfflineStatusText">Your internet connection is slow.</span>
				</Space>
			);
		}
		return (<></>);
	}, [showConnectionStatus, networkStats, offline]);

	const UserDetailsMenuSection = (
		<>
			<Menu.Item key="user-profile" disabled>
				<Space className="dropdown-user-profile">
					<div className="dropdown-user-avatar">
						<Avatar src={sessionData?.profilePic || userIcon} alt="user avatar" />
					</div>
					<Space direction="vertical" style={{ minWidth: '150px' }}>
						<span className="dropdown-user-name">{sessionData?.displayName || '-'}</span>
						<span className="dropdown-user-sub">{sessionData?.enrollmentId || sessionData?.email || '-'}</span>
					</Space>
				</Space>
			</Menu.Item>
			<Menu.Divider />
		</>
	);

	// The navigation popup panel
	const NavPopup = navPopupVisible && quizSegments && (
		<div
			style={{
				position: 'absolute',
				top: '100%',
				transform: 'translateX(-70%)',
				zIndex: 9999,
				backgroundColor: '#fff',
				borderRadius: '12px',
				boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
				minWidth: '300px',
				maxWidth: '350px',
				maxHeight: '60vh',
				overflowY: 'auto',
				padding: '8px 0',
				marginTop: '8px',
			}}
		>
			{quizSegments.map((segment) => {
				const isExpanded = expandedSegments.has(segment._id);
				const submittedCount = segment.questions.filter(
					(q) => q.status === QuestionStatus.Submitted,
				).length;
				const total = segment.questions.length;
				const isLocked = segment.isDisabled;
				const { isSubmitted } = segment;
				let color: string;
				if (isSubmitted) {
					color = '#2e7d32';
				} else if (isLocked) {
					color = '#bbb';
				} else {
					color = '#1a1a1a';
				}

				return (
					<div key={segment._id}>
						{/* Section header */}
						<button
							type="button"
							onClick={() => toggleSegment(segment._id)}
							style={{
								width: '100%',
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'space-between',
								padding: '10px 16px',
								background: isSubmitted ? '#e8f5e9' : '#fff',
								border: 'none',
								borderBottom: '1px solid #f0f0f0',
								cursor: 'pointer',
								gap: '8px',
							}}
						>
							<div style={{
								display: 'flex', alignItems: 'center', gap: '8px', flex: 1,
							}}
							>
								{isSubmitted && (
									<CheckCircleFilled style={{ color: '#4caf50', fontSize: '16px' }} />
								)}
								{isLocked && (
									<LockFilled style={{ color: '#bbb', fontSize: '14px' }} />
								)}
								<span style={{
									fontSize: '14px',
									fontWeight: 600,
									color,
									textAlign: 'left',
								}}
								>
									{segment.title}
								</span>
							</div>
							<div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
								<span style={{
									fontSize: '12px',
									color: isSubmitted ? '#4caf50' : '#888',
									fontWeight: 500,
								}}
								>
									{submittedCount}
									/
									{total}
								</span>
								{isExpanded
									? <UpOutlined style={{ fontSize: '10px', color: '#888' }} />
									: <DownOutlined style={{ fontSize: '10px', color: '#888' }} />}
							</div>
						</button>

						{/* Question grid */}
						{isExpanded && (
							<div style={{
								display: 'grid',
								gridTemplateColumns: 'repeat(5, 1fr)',
								gap: '8px',
								padding: '12px 14px',
								backgroundColor: '#fafafa',
								borderBottom: '1px solid #f0f0f0',
							}}
							>
								{segment.questions.map((q, qIdx) => {
									const isCurrent = q._id === currentQuestionId;
									const isQSubmitted = q.status === QuestionStatus.Submitted;
									// eslint-disable-next-line no-nested-ternary
									const circleBg = isCurrent ? '#de6834' : isQSubmitted ? '#e8f5e9' : '#fff';
									// eslint-disable-next-line no-nested-ternary
									const circleColor = isCurrent ? '#fff' : isQSubmitted ? '#2e7d32' : '#333';
									// eslint-disable-next-line no-nested-ternary
									const borderColor = isCurrent ? '1px solid #de6834' : isQSubmitted ? '1px solid #2e7d32' : '1px solid #e0e0e0';
									const to = isLocked ? '/dashboard' : `/test/${quizName}/attempt/${q._id}`;
									return (
										<Button
											style={{
												width: '44px',
												height: '44px',
												borderRadius: '50%',
												display: 'flex',
												alignItems: 'center',
												justifyContent: 'center',
												fontSize: '13px',
												fontWeight: 600,
												cursor: isLocked ? 'not-allowed' : 'pointer',
												border: borderColor,
												backgroundColor: circleBg,
												color: circleColor,
												transition: 'all 0.15s',
											}}
											disabled={isLocked}
											onClick={() => {
												history.push(to);
												setNavPopupVisible(false);
											}}
										>
											{qIdx + 1}
										</Button>
									);
								})}
							</div>
						)}
					</div>
				);
			})}
		</div>
	);

	return (
		<div style={{
			position: 'relative',
			zIndex: 99,
		}}
		>
			{identityCheckCode ? (
				<Space style={{ width: '100%', padding: '0.5em' }} justify="center" direction="horizontal">
					<span className="OfflineStatusText">Verification code:</span>
					<span style={{
						fontWeight: 800, letterSpacing: 4, fontSize: '20px', lineHeight: 1,
					}}
					>
						{identityCheckCode.split('').map((char, position) => (
							// eslint-disable-next-line react/no-array-index-key
							<span key={position} style={{ color: getIdentityCodeCharColor(char, position) }}>
								{char}
							</span>
						))}
					</span>
				</Space>
			) : ConnectionHeader}
			<div style={{
				backgroundColor: '#fff',
				borderBottomLeftRadius: '16px',
				borderBottomRightRadius: '16px',
				padding: '0.25rem',
				boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
			}}
			>
				{/* Top Row */}
				<div style={{
					display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px',
				}}
				>
					{/* Left Side: Back arrow + Logo + Title */}
					<div style={{
						display: 'flex', alignItems: 'center', gap: '12px', flex: '1 1 0', minWidth: 0,
					}}
					>
						<Space onClick={goToDashboard}>
							{Logo}
						</Space>
						<div style={{
							display: 'flex', flexDirection: 'column', minWidth: 0, flex: 1,
						}}
						>
							<span style={{
								fontSize: '16px',
								color: '#767676',
								display: 'block',
								overflow: 'hidden',
								textOverflow: 'ellipsis',
								whiteSpace: 'nowrap',
								fontWeight: 600,
								fontFamily: 'Hind, sans-serif',
							}}
							>
								{quizData?.title || quizName}
							</span>
							{hasQuizStarted && (
								<Tag
									round
									fill={timeLeft < 299 ? 'solid' : 'outline'}
									color={timeLeft < 299 ? 'danger' : 'default'}
									style={{ width: 'fit-content', padding: '4px 6px' }}
								>
									<ClockCircleOutlined style={{ marginRight: '4px' }} />
									{timerText}
								</Tag>
							)}
						</div>
					</div>

					{/* Center: User Info (name + enrollmentId) */}
					<div style={{
						display: 'flex',
						flexDirection: 'column',
						alignItems: 'center',
						flex: '1 1 0',
						justifyContent: 'center',
						minWidth: 0,
						textAlign: 'center',
					}}
					>
						<span style={{
							color: '#767676',
							fontWeight: 600,
							fontFamily: 'Hind, sans-serif',
							fontSize: '14px',
							lineHeight: 1.2,
							overflow: 'hidden',
							textOverflow: 'ellipsis',
							whiteSpace: 'nowrap',
							maxWidth: '100%',
						}}
						>
							{sessionData?.displayName}
						</span>
						{sessionData?.enrollmentId && (
							<span style={{
								color: '#767676',
								fontWeight: 600,
								fontFamily: 'Hind, sans-serif',
								fontSize: '14px',
								lineHeight: 1.2,
								overflow: 'hidden',
								textOverflow: 'ellipsis',
								whiteSpace: 'nowrap',
								maxWidth: '100%',
							}}
							>
								{sessionData?.enrollmentId}
							</span>
						)}
					</div>

					{/* Right Side: Profile */}
					<div style={{
						display: 'flex', alignItems: 'center', justifyContent: 'flex-end', flex: '1 1 0',
					}}
					>
						<Dropdown
							trigger={['click']}
							placement="bottomRight"
							overlayClassName="user-info-actions"
							overlay={(
								<Menu>
									{UserDetailsMenuSection}
									{isElectronApp && (
										<Menu.Item
											key="refresh"
											icon={<ReloadOutlined style={{ marginRight: 2, color: 'var(--primary-color)' }} />}
											onClick={refreshHandler}
										>
											<span style={{ fontSize: 16, color: '#848484' }}>Refresh</span>
										</Menu.Item>
									)}
									{!isElectronApp && (
										<Menu.Item
											key="user-logout"
											icon={<LogoutIcon style={{ marginRight: 2 }} height={16} />}
											onClick={logoutUser}
										>
											<span style={{ fontSize: 16, color: '#848484' }}>Logout</span>
										</Menu.Item>
									)}
								</Menu>
							)}
						>
							<div style={{
								display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer',
							}}
							>
								<Avatar
									src={sessionData?.profilePic || userIcon}
									alt="user avatar"
								/>
							</div>
						</Dropdown>
					</div>
				</div>

				{/* Divider */}
				<div style={{ height: '1px', backgroundColor: '#f0f0f0', margin: '8px 0 8px 0' }} />

				{/* Bottom Row */}
				<div style={{
					display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', padding: '0 0.25em 0.25em 0.25em',
				}}
				>
					{/* Left: Cheating Counters */}
					<div style={{
						display: 'flex', alignItems: 'center', gap: '4px', color: '#595959', fontSize: '14px',
					}}
					>
						{hasQuizStarted ? (
							<>
								<TagWithIcon icon={<FullscreenOutlined />} text={fullScreenOutCount || 0} />
								<TagWithIcon icon={<FullscreenExitOutlined />} text={fullScreenInCount || 0} />
								<TagWithIcon icon={<LogoutOutlined />} text={tabSwitchCount || 0} />
								<TagWithIcon icon={<LoginOutlined />} text={tabSwitchInCount || 0} />
							</>
						) : (
							<div />
						)}
					</div>

					{/* Right: Q-number pill + wifi */}
					<Space align="center" style={{ gap: '8px', width: '100%', padding: '0 0.5em' }} justify="end">
						{hasQuizStarted && (
							<div ref={popupRef} style={{ position: 'relative' }}>
								<Button
									type="button"
									onClick={() => setNavPopupVisible((v) => !v)}
									style={{
										border: '1.5px solid #ffccba',
										borderRadius: '16px',
										color: '#de6834',
										backgroundColor: '#fff3f0',
										padding: '4px 10px',
										cursor: 'pointer',
										fontSize: '13px',
									}}
								>
									<Space style={{ gap: 4, fontSize: '14px' }}>
										{isDashboardView ? (
											<span style={{
												display: 'inline-flex',
												alignItems: 'center',
												gap: '4px',
											}}
											>
												<CheckCircleOutlined />
												{totalAttemptedQuestions}
												/
												{totalQuizQuestions}
											</span>
										) : (
											<span>
												Q
												{currentQuestionNumber ?? '-'}
												/
												{totalQuestions}
											</span>
										)}
										{navPopupVisible && quizSegments
											? <UpOutlined style={{ fontSize: '10px' }} />
											: <DownOutlined style={{ fontSize: '10px' }} />}
									</Space>
								</Button>
								{NavPopup}
							</div>
						)}
					</Space>
				</div>
			</div>
			{isElectronApp && <FontChangeButton />}
		</div>
	);
};
